import type { CSSProperties, ReactNode } from 'react';
import { CONTROL_HINT, CONTROL_HINT_DASH, INSTRUCTIONS } from '@/game/configs';
import { Pause, Play } from 'lucide-react';
interface LobbyProps {
  bestScore: number;
  onStart: () => void;
}
interface HudProps {
  level: number;
  score: number;
  growth: number;
  dashCooldown: number;
  paused: boolean;
  onPause: () => void;
}
interface GameOverProps {
  result: {
    score: number;
    level: number;
    durationSeconds: number;
    isNewBest: boolean;
  };
  bestScore: number;
  onRestart: () => void;
}
const buttonClass =
  'pointer-events-auto min-h-14 cursor-pointer rounded-2xl bg-[#fb7185] px-7 text-lg font-extrabold text-[#082f49] shadow-[0_10px_30px_rgba(251,113,133,0.35)] transition duration-150 hover:bg-[#ff8798] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#fbbf24] focus-visible:outline-offset-4 active:scale-95';
const panelClass =
  'rounded-[28px] border border-white/20 bg-[#082f49]/80 shadow-2xl backdrop-blur-xl';
function Button({ children, onClick, label }: { children: ReactNode; onClick: () => void; label: string }) {
  return (
    <button type="button" className={buttonClass} onClick={onClick} aria-label={label}>
      {children}
    </button>
  );
}
export function Lobby({ bestScore, onStart }: LobbyProps) {
  return (
    <section className="relative z-20 flex h-full w-full flex-col items-center justify-center px-6 pb-[max(32px,env(safe-area-inset-bottom))] pt-[max(72px,env(safe-area-inset-top))]">
      <div className="max-w-[360px] text-center">
        <p className="mb-3 text-xs font-extrabold uppercase tracking-[0.3em] text-[#fbbf24]">Reef survival</p>
        <h1 className="text-6xl font-extrabold leading-[0.88] text-white drop-shadow-lg">
          Chomp<br /><span className="text-[#fb7185]">Tide</span>
        </h1>
        <div className={`${panelClass} mt-8 p-5 text-left`}>
          <p className="text-base font-bold leading-relaxed text-white">{INSTRUCTIONS}</p>
          <p className="mt-3 text-xs font-bold text-cyan-100">{CONTROL_HINT} · {CONTROL_HINT_DASH}</p>
          <div className="mt-4 flex items-center justify-between rounded-2xl bg-black/25 px-4 py-3">
            <span className="text-xs font-extrabold uppercase tracking-wider text-cyan-100">Personal best</span>
            <span className="text-2xl font-extrabold tabular-nums text-[#fbbf24]">{bestScore}</span>
          </div>
        </div>
        <div className="mt-7">
          <Button onClick={onStart} label="Start Chomp Tide">Dive In</Button>
        </div>
      </div>
    </section>
  );
}
export function Hud({ level, score, growth, dashCooldown, paused, onPause }: HudProps) {
  const style = {
    top: 'calc(12px + env(safe-area-inset-top, 0px))',
  } as CSSProperties;
  const dashReady = dashCooldown <= 0;
  return (
    <div
      className="pointer-events-none absolute left-3 right-3 z-10 flex items-start justify-between"
      style={style}
    >
      <div className={`${panelClass} min-w-[128px] px-4 py-2.5`}>
        <div className="flex items-center justify-between gap-3">
          <p className="text-[10px] font-extrabold uppercase tracking-widest text-cyan-100">Size</p>
          <p className="text-xl font-extrabold tabular-nums text-[#fbbf24]">Lv. {level}</p>
        </div>
        <div
          className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-black/35"
          role="progressbar"
          aria-label="Progress to next size"
          aria-valuemin={0}
          aria-valuemax={3}
          aria-valuenow={growth}
        >
          <div className="h-full rounded-full bg-[#fbbf24] transition-[width] duration-200" style={{ width: `${Math.min(100, (growth / 3) * 100)}%` }} />
        </div>
        <p className="mt-1 text-right text-[9px] font-bold tabular-nums text-cyan-100/80">{growth}/3 to grow</p>
      </div>
      <div className="flex items-start gap-2">
        <div className={`${panelClass} min-w-[92px] px-3 py-2.5 text-center`}>
          <p className="text-[10px] font-extrabold uppercase tracking-widest text-cyan-100">Eaten</p>
          <p className="text-xl font-extrabold tabular-nums text-white">{score}</p>
          <div className="mx-auto mt-1 h-1.5 w-16 overflow-hidden rounded-full bg-black/35">
            <div
              className={`h-full rounded-full transition-[width] ${dashReady ? 'bg-[#fbbf24]' : 'bg-cyan-300'}`}
              style={{ width: `${Math.max(0, Math.min(100, (1 - dashCooldown) * 100))}%` }}
            />
          </div>
          <p className={`mt-1 text-[9px] font-extrabold uppercase ${dashReady ? 'text-[#fbbf24]' : 'text-cyan-100'}`}>
            {dashReady ? 'Dash ready' : `${dashCooldown.toFixed(1)}s`}
          </p>
        </div>
        <button
          type="button"
          onClick={onPause}
          className="pointer-events-auto flex h-14 w-14 cursor-pointer items-center justify-center rounded-2xl border border-white/20 bg-[#082f49]/80 text-white shadow-lg backdrop-blur-xl transition hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#fbbf24] active:scale-90 active:bg-white/25"
          aria-label={paused ? 'Resume game' : 'Pause game'}
        >
          {paused ? <Play size={24} aria-hidden="true" /> : <Pause size={24} aria-hidden="true" />}
        </button>
      </div>
    </div>
  );
}
export function PauseOverlay({ onResume, onRestart }: { onResume: () => void; onRestart: () => void }) {
  return (
    <section className="absolute inset-0 z-20 flex h-full w-full items-center justify-center bg-black/65 px-6 backdrop-blur-md">
      <div className={`${panelClass} w-full max-w-[340px] p-6 text-center`}>
        <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-[#fbbf24]">Take a breath</p>
        <h2 className="mt-2 text-4xl font-extrabold text-white">Paused</h2>
        <p className="mt-3 text-sm text-cyan-100">Your fish is safe while the reef is paused.</p>
        <div className="mt-6 grid grid-cols-2 gap-3">
          <button type="button" onClick={onResume} className={`${buttonClass} px-4 text-base`}>Resume</button>
          <button
            type="button"
            onClick={onRestart}
            className="min-h-14 cursor-pointer rounded-2xl border border-white/20 bg-white/10 px-4 text-base font-extrabold text-white transition hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#fbbf24] active:scale-95"
          >
            Restart
          </button>
        </div>
      </div>
    </section>
  );
}
export function GameOver({ result, bestScore, onRestart }: GameOverProps) {
  return (
    <section className="absolute inset-0 z-20 flex h-full w-full items-center justify-center bg-black/70 px-6 backdrop-blur-md">
      <div className={`${panelClass} w-full max-w-[350px] p-6 text-center`}>
        {result.isNewBest ? (
          <div className="mx-auto mb-3 w-fit rounded-full bg-[#fbbf24] px-4 py-1 text-xs font-extrabold uppercase tracking-widest text-[#082f49]">New best</div>
        ) : null}
        <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-[#fb7185]">Tide won</p>
        <h2 className="mt-2 text-4xl font-extrabold text-white">Run over</h2>
        <p className="mt-3 text-sm leading-relaxed text-cyan-100">A larger fish ended your swim. The reef has bigger stories.</p>
        <div className="my-6 grid grid-cols-3 gap-2">
          <div className="rounded-2xl bg-black/25 p-3">
            <p className="text-[10px] font-bold uppercase text-cyan-100">Size</p>
            <p className="mt-1 text-xl font-extrabold text-[#fbbf24]">{result.level}</p>
          </div>
          <div className="rounded-2xl bg-black/25 p-3">
            <p className="text-[10px] font-bold uppercase text-cyan-100">Eaten</p>
            <p className="mt-1 text-xl font-extrabold text-white">{result.score}</p>
          </div>
          <div className="rounded-2xl bg-black/25 p-3">
            <p className="text-[10px] font-bold uppercase text-cyan-100">Best</p>
            <p className="mt-1 text-xl font-extrabold text-[#fb7185]">{bestScore}</p>
          </div>
        </div>
        <Button onClick={onRestart} label="Restart Chomp Tide">Swim Again</Button>
        <p className="mt-4 text-[11px] font-bold text-cyan-100">You can also press R to restart</p>
      </div>
    </section>
  );
}
